import { CSSProperties, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { css, styled } from 'styled-components';

import { theme } from '../../styles';
import { InfoIcon, MenuCloseIcon } from '../../assets/icons';
import { extendChildrenWithProps, useTabbing } from '../../utils';
import { Button } from '../Button';
import { Box, Container, Flex } from '../Layout';
import { useMediaMax } from '../Media';

export const mobileModalStyle: Modal.Styles = {
  content: {
    border: 'none',
    borderRadius: '0px',
    bottom: 'auto',
    boxShadow: 'none',
    left: '1.25rem',
    maxWidth: '100%',
    padding: '0rem',
    right: '1.25rem',
    top: '2.5rem',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: theme.zIndex.modal,
  },
};

export const desktopModalStyle: Modal.Styles = {
  content: {
    border: 'none',
    borderRadius: '0',
    bottom: 'auto',
    boxShadow: 'none',
    left: '50%',
    maxWidth: '100%',
    overflowY: 'auto',
    padding: '0rem',
    right: 'auto',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '41.125rem',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: theme.zIndex.modal,
  },
};

const CloseButton = styled.button<{ isTabbing: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  background-color: ${theme.colors.black};
  padding: 0;
  border: none;
  appearance: none;
  cursor: pointer;
  outline: none;

  &:hover {
    opacity: 0.9;
  }
  ${props =>
    props.isTabbing &&
    css`
      &:focus {
        outline: ${theme.colors.black} solid 2px;
        outline-offset: 5px;
      }
    `}
`;

type ForaModalProps = {
  /** Close modal  */
  closeModal?: () => void;
  /** Font Size */
  fontSize?: 'small' | 'standard' | 'medium';
  /** if set to `false` the button will not appear */
  hasCloseButton?: boolean;
  /** data-testid value for the Link for the Modal */
  testId?: string;

  /** Custom button to open the modal with */
  button?: JSX.Element;
  /** If no button is provided, these props are needed */
  /** textColorVariant of the Modal */
  textColorVariant?: 'light' | 'dark';
  /** Determines if the modal should be open or not */
  isOpen: boolean;
  /** Label text to display on the text for the Modal Link */
  label?: string;
  /** Open modal */
  openModal?: () => void;
  /** Boolean value that shows or hides the Link Icon */
  hasIcon?: boolean;
  /** Children prop where you send in `<Modal.Content />` */
  children: React.ReactNode;
};

if (process.env.NODE_ENV !== 'test') {
  try {
    if (process.env.STORYBOOK === 'true') {
      Modal.setAppElement('#storybook-root');
    } else {
      Modal.setAppElement('#root');
    }
  } catch (error) {
    console.error(error);
  }
}

export const ForaModal = ({
  closeModal,
  openModal,
  fontSize = 'standard',
  hasCloseButton = true,
  hasIcon = true,
  isOpen,
  button,
  label = 'Villkor',
  testId,
  textColorVariant = 'dark',
  children,
}: ForaModalProps) => {
  const isMobile = useMediaMax(theme.breakpoint.md);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isOpen, closeModal, openModal]);

  return (
    <>
      {button ? (
        button
      ) : (
        <Button
          testId={testId}
          styleAs="link"
          fontSize={fontSize}
          onClick={openModal ? openModal : undefined}
          color={textColorVariant === 'dark' ? 'foraBlue' : 'white'}
          style={{
            alignItems: 'center',
            alignSelf: 'flex-start',
            padding: 0,
            textAlign: 'left',
          }}
        >
          {hasIcon && <InfoIcon containerSize={22} marginRight={5} width={16} variant={textColorVariant} />}
          {label || 'Modal button'}
        </Button>
      )}
      <Modal
        isOpen={isOpen}
        onRequestClose={closeModal}
        style={isMobile ? mobileModalStyle : desktopModalStyle}
        contentLabel={label}
      >
        {extendChildrenWithProps(children, { closeModal, hasCloseButton, testId })}
      </Modal>
    </>
  );
};

const StyledContainer = styled(Container)`
  max-height: calc(-9rem + 100vh);

  overflow: auto;
  margin-bottom: 60px;
  // Make it possible to print multiple pages from react modal with scrolling content
  @media print {
    max-height: 100%;
  }
  // If ForaModal.Footer is sibling of ForaModal.Content
  &:not(:last-child) {
    margin-bottom: 0px;
  }
`;

type ForaModalContentProps = {
  /** Close modal  */
  closeModal?: () => void;
  /** if set to `false` the button will not appear */
  hasCloseButton?: boolean;
  /** Styling */
  style?: CSSProperties;
  /** data-testid value for the Link for the Modal */
  testId?: string;
  /** Children prop where you send in `<Modal.Content />` */
  children: React.ReactNode;
};

ForaModal.Content = ({ children, closeModal, hasCloseButton, style, testId }: ForaModalContentProps) => {
  const isTabbing = useTabbing();

  return (
    <>
      {hasCloseButton && (
        <Box position="absolute" style={{ top: 0, right: 0 }}>
          <CloseButton onClick={closeModal} isTabbing={isTabbing} data-testid={`${testId}-close-button`}>
            <MenuCloseIcon containerSize={40} width={20} />
          </CloseButton>
        </Box>
      )}

      <StyledContainer
        backgroundColor="white"
        paddingTop={60}
        paddingLeft={[20, 40, 40]}
        paddingRight={[20, 40, 40]}
        position={hasCloseButton ? 'static' : 'relative'}
        style={{ ...style }}
      >
        {children}
      </StyledContainer>
    </>
  );
};

type ModalFooterProps = {
  /** Sets button text of cancel button */
  cancelButtonText?: string;
  /** Sets button text of action button */
  actionButtonText?: string;
  /** Type of action button  */
  actionButtonVariant?: 'standard' | 'danger';
  /** On Action button click */
  onActionClick: () => void;
  /** Close modal  */
  onCancelClick: () => void;
};

const FooterButton = styled(Button)`
  @media screen and (min-width: ${theme.breakpoint.md}px) {
    flex-grow: 0;
  }
`;

ForaModal.Footer = ({
  cancelButtonText = 'Avbryt',
  actionButtonText = 'Spara',
  actionButtonVariant = 'standard',
  onActionClick,
  onCancelClick,
}: ModalFooterProps) => (
  <Flex
    paddingRight={[20, 40, 40]}
    paddingLeft={[20, 40, 40]}
    justifyContent="flex-end"
    columnGap={20}
    marginTop={40}
    marginBottom={40}
  >
    <FooterButton variant="outline" onClick={onCancelClick} fullWidth={false}>
      {cancelButtonText}
    </FooterButton>
    <FooterButton
      variant={actionButtonVariant === 'standard' ? 'primary' : 'burgundy'}
      onClick={onActionClick}
      fullWidth={false}
    >
      {actionButtonText}
    </FooterButton>
  </Flex>
);
